import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { hashKey } from '@tanstack/react-query'

// Metadata only. A cache writer cannot restore admission after reader refusal.
const listeners = new WeakMap<QueryClient, Set<() => void>>()
export function subscribeReaderRefusals(
  client: QueryClient,
  listener: () => void,
) {
  let entries = listeners.get(client)
  if (!entries) {
    entries = new Set()
    listeners.set(client, entries)
  }
  entries.add(listener)
  return () => {
    entries.delete(listener)
  }
}
function notify(client: QueryClient) {
  for (const listener of listeners.get(client) ?? []) listener()
}

const refusals = new WeakMap<QueryClient, Map<string, boolean>>()
function readerRefusals(client: QueryClient) {
  let entries = refusals.get(client)
  if (!entries) {
    entries = new Map()
    refusals.set(client, entries)
    const owned = entries
    client.getQueryCache().subscribe(event => {
      if (event.type === 'removed' && owned.delete(event.query.queryHash))
        notify(client)
      // Infinite readers recover only once the entire network fetch succeeds.
      // Manual cache writes cannot clear a prior refusal.
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        !event.action.manual &&
        owned.has(event.query.queryHash)
      ) {
        owned.set(event.query.queryHash, false)
        notify(client)
      }
    })
  }
  return entries
}
export function isNotificationReaderRefused(
  client: QueryClient,
  key: QueryKey,
) {
  return readerRefusals(client).get(hashKey(key)) === true
}
export function refuseNotificationReader(client: QueryClient, key: QueryKey) {
  readerRefusals(client).set(hashKey(key), true)
  notify(client)
}

/** Called only after a successful source reader; manual cache writes cannot
 * restore refusal admission through the query-cache subscription. */
export function recoverNotificationReader(client: QueryClient, key: QueryKey) {
  readerRefusals(client).set(hashKey(key), false)
  // Notify on the cache success event, after the fresh data is installed.
}

// Generic names for other readers using the same access-refusal registry.
export {
  isNotificationReaderRefused as isReaderRefused,
  refuseNotificationReader as refuseReader,
  recoverNotificationReader as recoverReader,
}
